import React, { useState } from 'react';
import {
  Box,
  Typography,
  Avatar,
  TextField,
  Button,
  Card,
  CardContent,
  Paper,
  Fade,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  MenuItem,
  IconButton,
} from '@mui/material';
import {
  Star,
  Work,
  Share,
  PhotoCamera,
  MoreVert,
  Edit,
  Delete,
  ChevronLeft,
  ChevronRight,
} from '@mui/icons-material';
import { getUserName, getUserInitials } from '../utils/userInfo';

const ShareYourThoughts = () => {
  const [openModal, setOpenModal] = useState(null);
  const [celebrateSubOption, setCelebrateSubOption] = useState(null);
  const [eventType, setEventType] = useState('online');
  const [eventName, setEventName] = useState('');
  const [eventDate, setEventDate] = useState('');
  const [eventTime, setEventTime] = useState('');
  const [eventDescription, setEventDescription] = useState('');
  const [eventLocation, setEventLocation] = useState('');
  const [eventRegistrationLink, setEventRegistrationLink] = useState('');
  const [postText, setPostText] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [userPosts, setUserPosts] = useState([]);
  const [editingPost, setEditingPost] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedPostId, setSelectedPostId] = useState(null);
  const [mainPostText, setMainPostText] = useState('');
  const [currentSlide, setCurrentSlide] = useState(0);

  const celebrateOptions = [
    'Project Launch',
    'Work Anniversary',
    'New Position',
    'Educational Milestone',
    'New Certification'
  ];

  const handlePostTypeClick = (type) => {
    setOpenModal(type);
    // Reset form data when opening modal
    setCelebrateSubOption(null);
    setPostText('');
    setImageFile(null);
    setEventName('');
    setEventDate('');
    setEventTime('');
    setEventDescription('');
    setEventLocation('');
    setEventRegistrationLink('');
    setEventType('online');
  };

  const handleCloseModal = () => {
    setOpenModal(null);
    setCelebrateSubOption(null);
    setPostText('');
    setImageFile(null);
    setEventName('');
    setEventDate('');
    setEventTime('');
    setEventDescription('');
    setEventLocation('');
    setEventRegistrationLink('');
    setEditingPost(null);
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    setImageFile(file);
  };

  const handleSendPost = () => {
    if (editingPost) {
      // Update existing post
      const updatedPost = {
        ...editingPost,
        content: postText,
        celebrationType: celebrateSubOption,
        eventDetails: openModal === 'event' ? {
          name: eventName,
          type: eventType,
          date: eventDate,
          time: eventTime,
          description: eventDescription,
          location: eventLocation,
          registrationLink: eventRegistrationLink
        } : null,
        image: imageFile,
        timestamp: editingPost.timestamp + ' (edited)'
      };

      setUserPosts(prevPosts => 
        prevPosts.map(post => 
          post.id === editingPost.id ? updatedPost : post
        )
      );
      setEditingPost(null);
    } else {
      // Create new post object
      const newPost = {
        id: Date.now(), // Simple ID generation
        author: userName,
        authorInitials: userInitials,
        timestamp: 'Just now',
        type: openModal,
        content: postText,
        celebrationType: celebrateSubOption,
        eventDetails: openModal === 'event' ? {
          name: eventName,
          type: eventType,
          date: eventDate,
          time: eventTime,
          description: eventDescription,
          location: eventLocation,
          registrationLink: eventRegistrationLink
        } : null,
        image: imageFile,
        likes: 0,
        comments: 0
      };

      // Add new post to the beginning of the posts array
      setUserPosts(prevPosts => [newPost, ...prevPosts]);
      // Center the new post in the slider
      setCurrentSlide(0);
    }
    
    // Close modal and reset form
    handleCloseModal();
  };

  const handleMenuOpen = (event, postId) => {
    setMenuAnchor(event.currentTarget);
    setSelectedPostId(postId);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedPostId(null);
  };

  const handleEditPost = () => {
    const postToEdit = userPosts.find(post => post.id === selectedPostId);
    if (postToEdit) {
      setEditingPost(postToEdit);
      setPostText(postToEdit.content);
      setCelebrateSubOption(postToEdit.celebrationType);
      setImageFile(postToEdit.image);
      
      if (postToEdit.eventDetails) {
        setEventName(postToEdit.eventDetails.name);
        setEventType(postToEdit.eventDetails.type);
        setEventDate(postToEdit.eventDetails.date);
        setEventTime(postToEdit.eventDetails.time);
        setEventDescription(postToEdit.eventDetails.description);
        setEventLocation(postToEdit.eventDetails.location || '');
        setEventRegistrationLink(postToEdit.eventDetails.registrationLink || '');
      }
      
      setOpenModal(postToEdit.type);
    }
    handleMenuClose();
  };

  const handleDeletePost = () => {
    setUserPosts(prevPosts => prevPosts.filter(post => post.id !== selectedPostId));
    handleMenuClose();
  };

  const handleMainPostSubmit = () => {
    if (mainPostText.trim()) {
      const newPost = {
        id: Date.now(),
        author: userName,
        authorInitials: userInitials,
        timestamp: 'Just now',
        type: 'anything',
        content: mainPostText,
        celebrationType: null,
        eventDetails: null,
        image: null,
        likes: 0,
        comments: 0
      };

      setUserPosts(prevPosts => [newPost, ...prevPosts]);
      setMainPostText('');
      // Center the new post in the slider
      setCurrentSlide(0);
    }
  };

  const handleSlideNext = () => {
    const allPosts = [...userPosts, ...examplePosts];
    if (currentSlide < Math.max(0, allPosts.length - 3)) {
      setCurrentSlide(currentSlide + 1);
    }
  };

  const handleSlidePrev = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  // Example posts to always show
  const examplePosts = [
    {
      id: 'example1',
      author: 'Arnav Simer',
      authorInitials: 'AS',
      timestamp: '2 hours ago',
      type: 'celebrate',
      content: 'Excited to announce the successful launch of our new KYC platform!  After months of hard work with an amazing team, we\'ve created something that will revolutionize how we manage employee information. Special thanks to everyone who contributed to this milestone!',
      celebrationType: 'Project Launch',
      eventDetails: null,
      image: null,
      likes: 12,
      comments: 5,
      avatarColor: '#2196F3'
    },
    {
      id: 'example2',
      author: 'Divya Mehta',
      authorInitials: 'DM',
      timestamp: '1 day ago',
      type: 'event',
      content: 'Join us for an interactive team building session focused on improving collaboration and communication. We\'ll have fun activities, networking opportunities, and valuable insights from our leadership team.',
      celebrationType: null,
      eventDetails: {
        name: 'Team Building Workshop',
        type: 'online',
        date: 'Oct 15, 2025',
        time: '2:00 PM',
        description: '',
        location: '',
        registrationLink: 'https://meet.google.com/abc-defg-hij'
      },
      image: null,
      likes: 8,
      comments: 3,
      avatarColor: '#FF9800'
    },
    {
      id: 'example3',
      author: 'Rishabh Gupta',
      authorInitials: 'RG',
      timestamp: '3 days ago',
      type: 'event',
      content: 'Join us for our monthly all-hands meeting where we\'ll discuss company updates, celebrate achievements, and share exciting news about upcoming projects.',
      celebrationType: null,
      eventDetails: {
        name: 'Monthly All-Hands Meeting',
        type: 'offline',
        date: 'Oct 20, 2025',
        time: '3:00 PM',
        description: '',
        location: 'Conference Room A, 2nd Floor',
        registrationLink: ''
      },
      image: null,
      likes: 15,
      comments: 7,
      avatarColor: '#4CAF50'
    }
  ];

  // Generate initials from name for circular avatar
  const getInitials = (name) => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Get user info from localStorage (set by OAuthCallback)
  const userName = getUserName();
  const userInitials = getUserInitials();

  return (
    <Box sx={{ width: '100%' }}>
      {/* Share Your Thoughts Section */}
      <Fade in timeout={800}>
        <Paper
          sx={{
            p: 3, borderRadius: 3, backgroundColor: 'white',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            border: '1px solid #e0e0e0',
            mb: 4,
          }}
        >
          {/* User Profile Row */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Avatar sx={{ bgcolor: '#FF0000', width: 48, height: 48, }}>
              {userInitials}
            </Avatar>
            <TextField
              fullWidth 
              placeholder="Share an update with your colleagues..."
              variant="outlined" 
              size="medium"
              multiline
              rows={2}
              value={mainPostText}
              onChange={(e) => setMainPostText(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleMainPostSubmit();
                }
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2, 
                  backgroundColor: '#f8fafc',
                  '& fieldset': { border: '1px solid #e2e8f0' },
                  '&:hover fieldset': { border: '1px solid #cbd5e1' },
                  '&.Mui-focused fieldset': { border: '2px solid #0D004A' },
                },
              }}
            />
          </Box>

          {/* Post Type Options */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 2, borderTop: '1px solid #e0e0e0' }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="text"
                startIcon={<Star />}
                onClick={() => handlePostTypeClick('celebrate')}
                sx={{
                  color: '#0D004A',
                  textTransform: 'none',
                  fontWeight: 'medium',
                  '&:hover': { backgroundColor: '#f0f0f0' },
                }}
              >
                Celebrate
              </Button>
              <Button
                variant="text"
                startIcon={<Work />}
                onClick={() => handlePostTypeClick('event')}
                sx={{
                  color: '#0D004A',
                  textTransform: 'none',
                  fontWeight: 'medium',
                  '&:hover': { backgroundColor: '#f0f0f0' },
                }}
              >
                Event
              </Button>

              <Button
                variant="text"
                startIcon={<Share />}
                onClick={() => handlePostTypeClick('anything')}
                sx={{
                  color: '#0D004A',
                  textTransform: 'none',
                  fontWeight: 'medium',
                  '&:hover': { backgroundColor: '#f0f0f0' },
                }}
              >
                Anything else
              </Button>
            </Box>
            
            <Button
              variant="contained"
              onClick={handleMainPostSubmit}
              disabled={!mainPostText.trim()}
              sx={{
                backgroundColor: '#0D004A',
                borderRadius: 2,
                px: 3,
                py: 1,
                textTransform: 'none',
                fontWeight: 'bold',
                '&:hover': {
                  backgroundColor: '#0A0038',
                  transform: 'translateY(-1px)',
                },
                '&:disabled': {
                  backgroundColor: '#ccc',
                  color: '#666',
                },
                transition: 'all 0.3s ease',
              }}
            >
              Post
            </Button>
          </Box>
        </Paper>
      </Fade>

      {/* Posts Grid - 3 Cards with Navigation */}
      <Box sx={{ position: 'relative', mb: 4 }}>
        {/* Navigation Arrows */}
        <IconButton
          onClick={handleSlidePrev}
          disabled={currentSlide === 0}
          sx={{
            position: 'absolute',
            left: -20,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 2,
            backgroundColor: 'white',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            '&:hover': { backgroundColor: '#f5f5f5' },
            '&:disabled': { opacity: 0.3 }
          }}
        >
          <ChevronLeft />
        </IconButton>

        <IconButton
          onClick={handleSlideNext}
          disabled={currentSlide >= Math.max(0, [...userPosts, ...examplePosts].length - 3)}
          sx={{
            position: 'absolute',
            right: -20,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 2,
            backgroundColor: 'white',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            '&:hover': { backgroundColor: '#f5f5f5' },
            '&:disabled': { opacity: 0.3 }
          }}
        >
          <ChevronRight />
        </IconButton>

        {/* Posts Container - Fixed 3 Cards with New Design */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 3 }}>
          {[...userPosts, ...examplePosts]
            .slice(currentSlide, currentSlide + 3)
            .map((post, index) => {
              const isUserPost = userPosts.some(p => p.id === post.id);
              
              return (
                <Fade key={post.id} in timeout={400}>
                  <Card
                    sx={{
                      borderRadius: 3,
                      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                      border: '1px solid #e0e0e0',
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%',
                      overflow: 'hidden',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
                      },
                      transition: 'all 0.3s ease',
                    }}
                  >
                    {/* Top Section with Dark Blue Background */}
                    <Box
                      sx={{
                        background: 'linear-gradient(135deg, #0D004A 0%, #1a0b5e 100%)',
                        p: 2.5,
                        pb: 1,
                        position: 'relative',
                        color: 'white',
                      }}
                    >
                      {/* Profile Row */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        {/* Circular Avatar with Initials */}
                        <Avatar 
                          sx={{ 
                            bgcolor: 'white',
                            color: '#0D004A',
                            width: 48, 
                            height: 48, 
                            fontSize: '1.2rem', 
                            fontWeight: 'bold',
                            border: '2px solid rgba(255,255,255,0.3)'
                          }}
                        >
                          {post.authorInitials || getInitials(post.author)}
                        </Avatar>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'white', fontSize: '1rem' }}>
                            {post.author}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.85rem' }}>
                            {post.timestamp}
                            {post.type === 'celebrate' && post.celebrationType && ` • 🚀 ${post.celebrationType}`}
                            {post.type === 'event' && ' • 📅 Event'}
                          </Typography>
                        </Box>
                        
                        {/* Edit/Delete Menu - Only for user posts */}
                        {isUserPost && (
                          <IconButton
                            onClick={(e) => handleMenuOpen(e, post.id)}
                            sx={{ 
                              color: 'white',
                              '&:hover': { 
                                backgroundColor: 'rgba(255,255,255,0.1)',
                              }
                            }}
                          >
                            <MoreVert />
                          </IconButton>
                        )}
                      </Box>
                    </Box>

                    {/* Content Section */}
                    <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', flexGrow: 1, backgroundColor: 'white' }}>
                      {/* Post Content */}
                      <Box sx={{ flexGrow: 1 }}>
                        {post.type === 'event' && post.eventDetails ? (
                          <Box sx={{ mb: 3 }}>
                            {/* Event Details - Left Aligned */}
                            <Box sx={{ mb: 2, textAlign: 'left' }}>
                              <Typography variant="body2" sx={{ color: '#333', mb: 0.5, fontWeight: 'medium', textAlign: 'left' }}>
                                <strong>Event name:</strong> {post.eventDetails.name}
                              </Typography>
                              <Typography variant="body2" sx={{ color: '#333', mb: 0.5, fontWeight: 'medium', textAlign: 'left' }}>
                                <strong>Date and Time:</strong> {post.eventDetails.date} • {post.eventDetails.time}
                              </Typography>
                              <Typography variant="body2" sx={{ color: '#333', mb: 0.5, fontWeight: 'medium', textAlign: 'left' }}>
                                <strong>Location:</strong> {post.eventDetails.type === 'online' ? 'Online' : post.eventDetails.location || 'TBD'}
                              </Typography>
                              {post.eventDetails.type === 'online' && (
                                <Typography variant="body2" sx={{ color: '#333', mb: 0.5, fontWeight: 'medium', textAlign: 'left' }}>
                                  <strong>Link:</strong> {post.eventDetails.registrationLink || 'https://...'}
                                </Typography>
                              )}
                            </Box>
                            
                            {/* Event Description */}
                            <Typography variant="body1" sx={{ lineHeight: 1.6, color: '#333', textAlign: 'left' }}>
                              {post.content}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.6, color: '#333', textAlign: 'left' }}>
                            {post.content}
                          </Typography>
                        )}
                        
                        {post.image && (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" sx={{ color: '#666', fontStyle: 'italic' }}>
                              📷 Image attached: {post.image.name}
                            </Typography>
                          </Box>
                        )}
                      </Box>

                      {/* Post Actions */}
                      <Box sx={{ display: 'flex', gap: 3, pt: 1, pb : 0.2, borderTop: '1px solid #e0e0e0', mt: 'auto' }}>
                        <Button 
                          size="small" 
                          sx={{ 
                            color: '#0D004A', 
                            textTransform: 'none', 
                            fontSize: '0.9rem',
                            fontWeight: 'medium',
                            display: 'body2',
                            alignItems: 'center',
                            gap: 0.5,
                            '&:hover': {
                              backgroundColor: 'rgba(13, 0, 74, 0.05)',
                            }
                          }}
                        >
                          👍 Like ({post.likes})
                        </Button>
                        <Button 
                          size="small" 
                          sx={{ 
                            color: '#0D004A', 
                            textTransform: 'none', 
                            fontSize: '0.9rem',
                            fontWeight: 'medium',
                            display: 'body2',
                            alignItems: 'center',
                            gap: 0.5,
                            '&:hover': {
                              backgroundColor: 'rgba(13, 0, 74, 0.05)',
                            }
                          }}
                        >
                          💬 Comment ({post.comments})
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Fade>
              );
            })}
        </Box>
      </Box>

      {/* Edit/Delete Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            borderRadius: 2,
            minWidth: 150
          }
        }}
      >
        <MenuItem 
          onClick={handleEditPost}
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1.5,
            '&:hover': { backgroundColor: '#f5f5f5' }
          }}
        >
          <Edit fontSize="small" sx={{ color: '#0D004A' }} />
          <Typography variant="body2">Edit Post</Typography>
        </MenuItem>
        <MenuItem 
          onClick={handleDeletePost}
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1.5,
            '&:hover': { backgroundColor: '#ffebee' }
          }}
        >
          <Delete fontSize="small" sx={{ color: '#f44336' }} />
          <Typography variant="body2" sx={{ color: '#f44336' }}>Delete Post</Typography>
        </MenuItem>
      </Menu>

      {/* Modals for Post Types */}
      {/* Celebrate Modal */}
      <Dialog
        open={openModal === 'celebrate'}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ color: '#0D004A', fontWeight: 'bold', borderBottom: '1px solid #e0e0e0' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Star sx={{ color: '#0D004A' }} />
            What are you celebrating?
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
            {celebrateOptions.map((option) => (
              <Button
                key={option}
                variant={celebrateSubOption === option ? 'contained' : 'outlined'}
                onClick={() => setCelebrateSubOption(option)}
                sx={{
                  textTransform: 'none',
                  borderRadius: 2,
                  backgroundColor: celebrateSubOption === option ? '#0D004A' : 'white',
                  color: celebrateSubOption === option ? 'white' : '#0D004A',
                  borderColor: '#0D004A',
                  '&:hover': {
                    backgroundColor: celebrateSubOption === option ? '#0A0038' : '#f0f0f0',
                  },
                }}
              >
                {option}
              </Button>
            ))}
          </Box>
          
          {celebrateSubOption && (
            <Box>
              <Button
                variant="outlined"
                startIcon={<PhotoCamera />}
                component="label"
                sx={{
                  mb: 2,
                  mr: 2,
                  textTransform: 'none',
                  borderColor: '#0D004A',
                  color: '#0D004A',
                  '&:hover': { backgroundColor: '#f0f0f0' },
                }}
              >
                Add Image
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleImageUpload}
                />
              </Button>
              {imageFile && (
                <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
                  Selected: {imageFile.name}
                </Typography>
              )}
              
              <TextField
                fullWidth
                multiline
                rows={4}
                placeholder={`Tell us about your ${celebrateSubOption.toLowerCase()}...`}
                variant="outlined"
                value={postText}
                onChange={(e) => setPostText(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                    '& fieldset': { borderColor: '#e2e8f0' },
                    '&:hover fieldset': { borderColor: '#cbd5e1' },
                    '&.Mui-focused fieldset': { borderColor: '#0D004A' },
                  },
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid #e0e0e0' }}>
          <Button
            onClick={handleCloseModal}
            sx={{ color: '#666', textTransform: 'none' }}
          >
            Cancel
          </Button>
           <Button
             variant="contained"
             onClick={handleSendPost}
             disabled={!celebrateSubOption}
             sx={{
               backgroundColor: '#0D004A',
               textTransform: 'none',
               '&:hover': { backgroundColor: '#0A0038' },
               '&:disabled': { backgroundColor: '#ccc', color: '#666' },
             }}
           >
             {editingPost ? 'Update' : 'Send'}
           </Button>
        </DialogActions>
      </Dialog>

      {/* Event Modal */}
      <Dialog
        open={openModal === 'event'}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ color: '#0D004A', fontWeight: 'bold', borderBottom: '1px solid #e0e0e0' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Work sx={{ color: '#0D004A' }} />
            Event Details
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <Button
              variant={eventType === 'online' ? 'contained' : 'outlined'}
              onClick={() => setEventType('online')}
              sx={{
                textTransform: 'none',
                backgroundColor: eventType === 'online' ? '#0D004A' : 'white',
                color: eventType === 'online' ? 'white' : '#0D004A',
                borderColor: '#0D004A',
              }}
            >
              Online Event
            </Button>
            <Button
              variant={eventType === 'offline' ? 'contained' : 'outlined'}
              onClick={() => setEventType('offline')}
              sx={{
                textTransform: 'none',
                backgroundColor: eventType === 'offline' ? '#0D004A' : 'white',
                color: eventType === 'offline' ? 'white' : '#0D004A',
                borderColor: '#0D004A',
              }}
            >
              Offline Event
            </Button>
          </Box>

          <TextField
            fullWidth
            placeholder="Event Name"
            variant="outlined"
            value={eventName}
            onChange={(e) => setEventName(e.target.value)}
            sx={{ mb: 3 }}
          />

          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <TextField
              type="date"
              label="Event Date"
              variant="outlined"
              value={eventDate}
              onChange={(e) => setEventDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ flex: 1 }}
            />
            <TextField
              type="time"
              label="Event Time"
              variant="outlined"
              value={eventTime}
              onChange={(e) => setEventTime(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ flex: 1 }}
            />
          </Box>

          {/* Location/Registration Link based on event type */}
          {eventType === 'offline' ? (
            <TextField
              fullWidth
              placeholder="Event Location (e.g., Conference Room A, 123 Main St)"
              variant="outlined"
              value={eventLocation}
              onChange={(e) => setEventLocation(e.target.value)}
              sx={{ mb: 3 }}
            />
          ) : (
            <TextField
              fullWidth
              placeholder="Registration Link (e.g., https://meet.google.com/abc-defg-hij)"
              variant="outlined"
              value={eventRegistrationLink}
              onChange={(e) => setEventRegistrationLink(e.target.value)}
              sx={{ mb: 3 }}
            />
          )}

          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder="Event Description"
            variant="outlined"
            value={eventDescription}
            onChange={(e) => setEventDescription(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'white',
                '& fieldset': { borderColor: '#e2e8f0' },
                '&:hover fieldset': { borderColor: '#cbd5e1' },
                '&.Mui-focused fieldset': { borderColor: '#0D004A' },
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid #e0e0e0' }}>
          <Button
            onClick={handleCloseModal}
            sx={{ color: '#666', textTransform: 'none' }}
          >
            Cancel
          </Button>
           <Button
             variant="contained"
             onClick={handleSendPost}
             disabled={!eventName || !eventDate || !eventTime}
             sx={{
               backgroundColor: '#0D004A',
               textTransform: 'none',
               '&:hover': { backgroundColor: '#0A0038' },
               '&:disabled': { backgroundColor: '#ccc', color: '#666' },
             }}
           >
             {editingPost ? 'Update' : 'Send'}
           </Button>
        </DialogActions>
      </Dialog>

      {/* Anything Else Modal */}
      <Dialog
        open={openModal === 'anything'}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ color: '#0D004A', fontWeight: 'bold', borderBottom: '1px solid #e0e0e0' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Share sx={{ color: '#0D004A' }} />
            Share your thoughts
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <TextField
            fullWidth
            multiline
            rows={6}
            placeholder="What's on your mind?"
            variant="outlined"
            value={postText}
            onChange={(e) => setPostText(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'white',
                '& fieldset': { borderColor: '#e2e8f0' },
                '&:hover fieldset': { borderColor: '#cbd5e1' },
                '&.Mui-focused fieldset': { borderColor: '#0D004A' },
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid #e0e0e0' }}>
          <Button
            onClick={handleCloseModal}
            sx={{ color: '#666', textTransform: 'none' }}
          >
            Cancel
          </Button>
           <Button
             variant="contained"
             onClick={handleSendPost}
             disabled={!postText.trim()}
             sx={{
               backgroundColor: '#0D004A',
               textTransform: 'none',
               '&:hover': { backgroundColor: '#0A0038' },
               '&:disabled': { backgroundColor: '#ccc', color: '#666' },
             }}
           >
             {editingPost ? 'Update' : 'Send'}
           </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ShareYourThoughts;
